/**
 * Escapes CSV field values by wrapping in quotes and escaping internal quotes
 * @param {any} field - The field value to escape
 * @returns {string} - Escaped CSV field
 */
export const escapeCSV = (field) => {
  if (field === null || field === undefined) {
    return '""';
  }

  const stringField = String(field);
  const containsSpecialCharacters = stringField.includes('"')
    || stringField.includes(',')
    || stringField.includes('\n');

  if (containsSpecialCharacters) {
    return `"${stringField.replace(/"/g, '""')}"`;
  }

  return `"${stringField}"`;
};

/**
 * Extracts vote counts based on question type
 * @param {Object} question - Question object with controlView property
 * @param {Object} answer - Answer object with count/controlCount/exposedCount
 * @returns {Object} - {control: number, exposed: number}
 */
export const getVoteCounts = (question, answer) =>
  question.controlView
    ? { control: answer.controlCount || 0, exposed: answer.exposedCount || 0 }
    : { control: 0, exposed: answer.count || 0 };

/**
 * Extracts benchmark values for a question, returning empty strings when unavailable.
 * @param {Object|null} benchmark - Benchmark object with pooledMetrics, or null
 * @returns {Array} - [absoluteLift, relativeLift, marginOfError, statisticalSignificance]
 */
const getBenchmarkValues = (benchmark) => {
  if (!benchmark || !benchmark.pooledMetrics) return ['', '', '', ''];
  const pm = benchmark.pooledMetrics;
  return [
    pm.absoluteLift != null ? pm.absoluteLift.toFixed(2) : '',
    pm.relativeLift != null ? pm.relativeLift.toFixed(2) : '',
    pm.marginOfError != null ? pm.marginOfError.toFixed(2) : '',
    pm.isStatisticallySignificant != null ? pm.isStatisticallySignificant : '',
  ];
};

/**
 * Creates a CSV row for a question-answer pair
 * @param {string} campaignId - Campaign identifier
 * @param {string} campaignName - Campaign name
 * @param {Object} question - Question object
 * @param {Object} answer - Answer object
 * @param {Object|null} [benchmark] - Benchmark data for this question
 * @returns {string} - CSV row string
 */
export const createCSVRow = (campaignId, campaignName, question, answer, benchmark, benchmarkMeta = {}) => {
  const { control, exposed } = getVoteCounts(question, answer);
  const fields = [
    campaignId,
    question._id,
    answer._id,
    campaignName,
    question.question,
    answer.answer,
    answer.isCorrect ?? false,
  ];
  const benchmarkValues = getBenchmarkValues(benchmark);
  const metaValues = [benchmarkMeta.country || '', benchmarkMeta.industry || ''];
  return fields.map(escapeCSV).concat([control, exposed]).concat(benchmarkValues.map(escapeCSV)).concat(metaValues.map(escapeCSV)).join(',');
};

/**
 * Gets all question-answer pairs flattened into array
 * @param {Array} questions - Array of question objects
 * @param {Array} answers - Array of answer objects
 * @returns {Array} - Array of {question, answer} pairs
 */
export const getQuestionAnswerPairs = (questions, answers) =>
  questions.flatMap(question =>
    answers
      .filter(answer => answer.questionId === question._id)
      .map(answer => ({ question, answer }))
  );

/**
 * Generates complete CSV data string
 * @param {Object} campaignData - {campaignId, campaignName}
 * @param {Array} questions - Array of question objects
 * @param {Array} answers - Array of answer objects
 * @param {Record<string, object|null>} [benchmarks={}] - Benchmark data keyed by questionId
 * @param {{ country: string, industry: string }} [benchmarkMeta={}] - Country and industry for benchmark context
 * @returns {string} - Complete CSV string with header and data
 */
export const generateCSVData = (campaignData, questions, answers, benchmarks = {}, benchmarkMeta = {}) => {
  const { campaignId, campaignName } = campaignData;

  const header = 'bls_campaign_id,bls_question_id,bls_answer_id,campaign_name,question,answer,preferred,votes_control,votes_exposed,benchmark_absolute_lift,benchmark_relative_lift,benchmark_margin_of_error,benchmark_statistical_significance,benchmark_country,benchmark_industry';

  const questionAnswerPairs = getQuestionAnswerPairs(questions, answers);

  const rows = questionAnswerPairs.map(({ question, answer }) =>
    createCSVRow(campaignId, campaignName, question, answer, benchmarks[question._id], benchmarkMeta)
  );

  return [header, ...rows].join('\n');
};

/**
 * Downloads CSV data as file (has side effects - harder to test)
 * @param {string} csvData - CSV data string
 * @param {string} filename - Filename for download
 */
export const downloadCSVFile = (csvData, filename) => {
  const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');

  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};